import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
  MenuItem,
} from "@mui/material";
import useUpdate from "../../api/useUpdate";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import { getUniqueCurrencies } from "../../utils/currencyOptions";
import { useAuth } from "../../context/AuthContext";
import { useTenant } from "../../context/TenantContext";

const PassiveIncomeEdit = ({ open, onClose, editData, token, onSuccess }) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const { shop: currentShop } = useTenant();
  const { handleUpdate, loading, isSuccess } = useUpdate("shop/passive-incomes", token);
  const selectedBranch = auth?.user?.selected_branch
    ? String(auth.user.selected_branch)
    : auth?.user?.user?.selected_branch
      ? String(auth.user.user.selected_branch)
      : "";
  const shopId =
    selectedBranch ||
    (currentShop?.id ? String(currentShop.id) : "") ||
    (editData?.shop_details?.id ? String(editData.shop_details.id) : "") ||
    (editData?.shop ? String(editData.shop) : "");

  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [selectedCurrencyId, setSelectedCurrencyId] = useState("");
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [exchangeRate, setExchangeRate] = useState("");
  const [incomeDate, setIncomeDate] = useState("");
  const [notes, setNotes] = useState("");

  const { data: accountsData, isLoading: accountLoading } = useFetchObjects(
    ["passive-income-accounts", "edit", shopId],
    shopId ? `shop/accounts/?page_size=all&shop=${shopId}` : "shop/accounts/?page_size=all",
    token
  );
  const accounts = accountsData?.results || accountsData || [];
  const shopAccounts = useMemo(
    () =>
      shopId
        ? accounts.filter((account) => String(account.shop) === String(shopId))
        : accounts,
    [accounts, shopId]
  );
  const selectedAccount = useMemo(
    () =>
      shopAccounts.find((item) => String(item.id) === String(selectedAccountId)) ||
      accounts.find((item) => String(item.id) === String(selectedAccountId)) ||
      null,
    [accounts, selectedAccountId, shopAccounts]
  );
  const selectedShopId = selectedAccount?.shop ? String(selectedAccount.shop) : shopId;

  const { data: currenciesData, isLoading: currencyLoading } = useFetchObjects(
    ["passive-income-currencies", "edit", selectedShopId],
    selectedShopId ? `shop/currencies?page_size=all&shop=${selectedShopId}` : "shop/currencies?page_size=all",
    token
  );
  const currencies = getUniqueCurrencies(currenciesData?.results || currenciesData || []);
  const filteredCurrencies = selectedShopId
    ? currencies.filter((cur) => String(cur.shop) === String(selectedShopId))
    : currencies;

  const { data: directRateData } = useFetchObjects(
    ["passive-income-rate-direct", selectedShopId, selectedCurrencyId, selectedAccount?.currency],
    selectedShopId && selectedCurrencyId && selectedAccount?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${selectedShopId}&from_currency=${selectedCurrencyId}&to_currency=${selectedAccount.currency}`
      : "",
    token
  );
  const { data: reverseRateData } = useFetchObjects(
    ["passive-income-rate-reverse", selectedShopId, selectedCurrencyId, selectedAccount?.currency],
    selectedShopId && selectedCurrencyId && selectedAccount?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${selectedShopId}&from_currency=${selectedAccount.currency}&to_currency=${selectedCurrencyId}`
      : "",
    token
  );

  const exchangeRatePreview = useMemo(() => {
    if (!selectedCurrencyId) return "";
    const accountCurrencyId = selectedAccount?.currency ?? selectedCurrencyId;
    if (String(selectedCurrencyId) === String(accountCurrencyId)) return "1";
    const direct = directRateData?.results?.[0]?.rate;
    if (direct) return String(direct);
    const reverse = reverseRateData?.results?.[0]?.rate;
    if (reverse && Number(reverse) > 0) return String(1 / Number(reverse));
    return "";
  }, [selectedCurrencyId, selectedAccount, directRateData, reverseRateData]);

  useEffect(() => {
    setExchangeRate(exchangeRatePreview);
  }, [exchangeRatePreview]);

  useEffect(() => {
    if (!editData) return;
    setName(editData.name || "");
    setAmount(editData.amount != null ? String(editData.amount) : "");
    setIncomeDate(
      editData.income_date ? new Date(editData.income_date).toISOString().slice(0, 16) : ""
    );
    setNotes(editData.notes || "");
    setSelectedAccountId(String(editData.account_details?.id || editData.account || ""));
    setSelectedCurrencyId(String(editData.currency_details?.id || editData.currency || ""));
    setExchangeRate(editData.exchange_rate != null ? String(editData.exchange_rate) : "");
  }, [editData, open]);

  useEffect(() => {
    if (!selectedAccountId) {
      setSelectedCurrencyId("");
      return;
    }
    if (
      selectedCurrencyId &&
      filteredCurrencies.some((cur) => String(cur.id) === String(selectedCurrencyId))
    ) {
      return;
    }
    const preferred = filteredCurrencies.find(
      (cur) => String(cur.id) === String(selectedAccount?.currency)
    ) || filteredCurrencies[0];
    setSelectedCurrencyId(preferred ? String(preferred.id) : "");
  }, [selectedAccountId, filteredCurrencies, selectedAccount?.currency, selectedCurrencyId]);

  const convertedAmount = useMemo(() => {
    const nAmount = Number(amount);
    const nRate = Number(exchangeRate);
    if (!Number.isFinite(nAmount) || !Number.isFinite(nRate) || nAmount <= 0 || nRate <= 0) {
      return "";
    }
    return (nAmount * nRate).toFixed(2);
  }, [amount, exchangeRate]);

  const handleSubmit = () => {
    if (!editData?.id) return;
    const formData = new FormData();
    formData.append("shop", selectedShopId);
    formData.append("name", name);
    formData.append("account", selectedAccountId);
    formData.append("currency", selectedCurrencyId);
    formData.append("amount", amount);
    formData.append("exchange_rate", exchangeRate);
    formData.append("income_date", incomeDate);
    formData.append("notes", notes);
    handleUpdate(editData.id, formData);
  };

  useEffect(() => {
    if (isSuccess) {
      onSuccess?.();
      onClose();
    }
  }, [isSuccess, onClose, onSuccess]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("passiveIncome.editTitle", "Edit Passive Income")}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField
            margin="dense"
            label={t("passiveIncome.name", "Name")}
            fullWidth
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            select
            margin="dense"
            label={t("passiveIncome.account", "Account")}
            fullWidth
            required
            value={selectedAccountId}
            onChange={(e) => setSelectedAccountId(e.target.value)}
            disabled={accountLoading}
          >
            {accountLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              shopAccounts.map((account) => (
                <MenuItem key={account.id} value={String(account.id)}>
                  {account.name} ({account.currency_title || "-"})
                </MenuItem>
              ))
            )}
          </TextField>
          {selectedAccount && (
            <Box sx={{ px: 1, mt: -0.5 }}>
              <Box component="span" sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                {t("passiveIncome.availableBalance", "Available Balance")}:
              </Box>{" "}
              <Box component="span" sx={{ fontSize: "0.75rem", fontWeight: 600 }}>
                {selectedAccount.balance != null ? selectedAccount.balance : "-"}{" "}
                {selectedAccount.currency_title || ""}
              </Box>
            </Box>
          )}
          <TextField
            select
            margin="dense"
            label={t("passiveIncome.currency", "Currency")}
            fullWidth
            required
            value={selectedCurrencyId}
            onChange={(e) => setSelectedCurrencyId(e.target.value)}
            disabled={!selectedShopId || currencyLoading}
          >
            {currencyLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              filteredCurrencies.map((currency) => (
                <MenuItem key={currency.id} value={String(currency.id)}>
                  {currency.title}
                </MenuItem>
              ))
            )}
          </TextField>
          <TextField
            margin="dense"
            label={t("passiveIncome.amount", "Amount")}
            type="number"
            fullWidth
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputProps={{ min: 0, step: "0.01" }}
          />
          <TextField
            margin="dense"
            label={t("passiveIncome.exchangeRate", "Exchange Rate")}
            type="number"
            fullWidth
            required
            value={exchangeRate}
            onChange={(e) => setExchangeRate(e.target.value)}
            inputProps={{ min: 0, step: "0.000001" }}
          />
          <TextField
            margin="dense"
            label={t("passiveIncome.convertedAmount", "Converted Amount")}
            fullWidth
            value={convertedAmount}
            disabled
          />
          <TextField
            margin="dense"
            label={t("passiveIncome.incomeDate", "Income Date")}
            type="datetime-local"
            fullWidth
            required
            value={incomeDate}
            onChange={(e) => setIncomeDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            margin="dense"
            label={t("passiveIncome.notes", "Notes")}
            fullWidth
            multiline
            minRows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common.cancel", "Cancel")}</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={20} /> : t("common.save", "Save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PassiveIncomeEdit;

